/**
 * The open-redirect guards and the returnTo round trip of the system surfaces (layered shell
 * surfaces, R-8; WS-7 §3.2).
 *
 * Moved from the shell's `logic/auth-utils.ts`: one open-redirect rule for the login and the logout
 * flow, a landing that is never the logout route, and a stored returnTo that is validated again on
 * every read (AD-12, #2569). Customer onboarding S5 (WS-15) added one landing rule: a landing is
 * never the tenant hand-off callback page either (`isTenantHandoffCallbackTarget`). `protocol.ts`
 * re-exports `buildLoginRedirectPath` and `resolvePostLoginPath`, and its `readSystemOutput` judges
 * every output `landing` with the same guard, so the loader and the bundle share one rule.
 *
 * The module also holds the per-flow OAuth marker that binds a callback to the tab that started
 * the flow (login CSRF, PR #3152).
 *
 * The module reads `document.baseURI` and `sessionStorage`, and `leaveDocument` assigns a
 * location. Nothing else here has a side effect, and nothing throws.
 */

import { TENANT_HANDOFF_CALLBACK_PATH } from './tenant-handoff-wire.js';

/** sessionStorage key carrying returnTo across the IdP round trip. */
export const RETURN_TO_STORAGE_KEY = 'app_return_to';

/** The key a shell before the storage-key rename wrote; read once and removed by {@link consumeReturnTo}. */
const LEGACY_RETURN_TO_STORAGE_KEY = 'zion_return_to';

/** The system logout route. A landing target is never this route (see {@link isSafeReturnTo}'s callers). */
const LOGOUT_PATH = '/logout';

/** Where a login lands when its returnTo is absent or unsafe. */
const DEFAULT_LOGIN_LANDING = '/home';

/** Where a logout lands when its redirect is absent or unsafe. */
const DEFAULT_LOGOUT_LANDING = '/login';

/** The schemes {@link leaveDocument} refuses: each runs or reads content in the page's context. */
const REFUSED_SCHEMES: readonly string[] = Object.freeze(['javascript:', 'data:', 'vbscript:', 'blob:', 'file:']);

/**
 * Open-redirect guard for `?returnTo=` and `?redirect=` values and for every output `landing`.
 *
 * True only for a same-origin relative path. The value must start with `/`, and its second
 * character must not be `/` or `\` (a browser reads `/\evil.com` as `//evil.com`). The value is
 * then resolved against `document.baseURI` and its origin must equal the page's: the URL parser
 * strips tab, CR and LF before parsing, so `/<TAB>/evil.com` passes the character tests and
 * resolves off-origin. A value the parser throws on (`/<TAB>/[`) answers false. Never throws.
 */
export function isSafeReturnTo(path: string | null | undefined): path is string {
  if (!path) return false;
  if (path.charAt(0) !== '/') return false;
  const second = path.charAt(1);
  if (second === '/' || second === '\\') return false;
  try {
    const base = document.baseURI;
    return new URL(path, base).origin === new URL(base).origin;
  } catch {
    return false;
  }
}

/**
 * True when a path that {@link isSafeReturnTo} accepted resolves to the logout route: `/logout`,
 * `/logout/`, `/logout/x` or `/logout?redirect=…`, and never `/logout-report`. A landing on the
 * logout route signs the user out again, and `/logout?redirect=/logout` never ends.
 *
 * The comparison ignores case (R-75), because `matchSystemRoute` and React Router both fold case:
 * `/LOGOUT` and `/Logout/x` mount the logout surface, so each is a logout target here too. The
 * URL parser does not decode a percent escape in the path, and `matchSystemRoute` does not
 * either, so `/%6Cogout` is not the logout route for either of them.
 *
 * Precondition: the caller has run {@link isSafeReturnTo} on `path`, which parsed the same string
 * against the same base, so the parse here cannot throw.
 */
function isLogoutTarget(path: string): boolean {
  const pathname = new URL(path, document.baseURI).pathname.toLowerCase();
  return pathname === LOGOUT_PATH || pathname.startsWith(`${LOGOUT_PATH}/`);
}

/**
 * True when a path that {@link isSafeReturnTo} accepted resolves to the tenant hand-off callback
 * page, `TENANT_HANDOFF_CALLBACK_PATH` (customer onboarding S5, WS-15, R-062, R-064): the path case
 * folded, with one trailing slash removed, the rule of the shell boot's
 * `isTenantHandoffCallbackPath`. The URL parser resolves dot segments and a backslash as the
 * browser does for `history.replaceState`, so `/auth/./handoff-callback` and
 * `/auth\handoff-callback` are the callback page too. A landing there makes the boot redeem a
 * second time: with no fragment it shows the hand-off error to a signed-in user, and with a
 * fragment of another code it replaces the session the tab just adopted.
 *
 * Precondition: the caller has run {@link isSafeReturnTo} on `path`, so the parse cannot throw.
 */
function isTenantHandoffCallbackTarget(path: string): boolean {
  const pathname = new URL(path, document.baseURI).pathname.toLowerCase();
  const trimmed = pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  return trimmed === TENANT_HANDOFF_CALLBACK_PATH;
}

/** True when a safe path may be a landing: it is neither the logout route nor the tenant hand-off callback page. */
function isLandingTarget(path: string): boolean {
  return !isLogoutTarget(path) && !isTenantHandoffCallbackTarget(path);
}

/**
 * Where a login lands: `returnTo` when it is a safe path that is neither the logout route nor the
 * tenant hand-off callback page, else `/home`. Every consumption point (the `?returnTo=` read and
 * the stored value) goes through it.
 */
export function resolvePostLoginPath(returnTo: string | null | undefined): string {
  if (isSafeReturnTo(returnTo) && isLandingTarget(returnTo)) return returnTo;
  return DEFAULT_LOGIN_LANDING;
}

/** Where a logout lands: `redirect` under the same rule as {@link resolvePostLoginPath}, else `/login`. */
export function resolvePostLogoutPath(redirect: string | null | undefined): string {
  if (isSafeReturnTo(redirect) && isLandingTarget(redirect)) return redirect;
  return DEFAULT_LOGOUT_LANDING;
}

/**
 * The `/login` target that brings the user back to `pathname` + `search` after signing in:
 * `/login?returnTo=<encoded>`, or a bare `/login` when the origin is `/` or empty (it would land
 * on `/home` anyway).
 */
export function buildLoginRedirectPath(pathname: string, search: string): string {
  const origin = `${pathname}${search}`;
  if (origin === '/' || origin === '') return DEFAULT_LOGOUT_LANDING;
  return `${DEFAULT_LOGOUT_LANDING}?returnTo=${encodeURIComponent(origin)}`;
}

/**
 * Keep a returnTo across the IdP round trip, which leaves the origin and drops the query. A safe
 * value is stored under {@link RETURN_TO_STORAGE_KEY}; an absent or unsafe value clears the key,
 * so a stale target from an earlier attempt cannot reach this login. A storage that throws
 * (private mode, disabled storage) leaves nothing stored and is swallowed.
 */
export function persistReturnTo(value: string | null): void {
  try {
    if (isSafeReturnTo(value)) {
      sessionStorage.setItem(RETURN_TO_STORAGE_KEY, value);
      return;
    }
    sessionStorage.removeItem(RETURN_TO_STORAGE_KEY);
  } catch {
    // Storage unavailable: nothing persisted, which reads back as "no returnTo".
  }
}

/**
 * Read and clear the stored returnTo after the IdP round trip, and answer where the login lands.
 * Reads {@link RETURN_TO_STORAGE_KEY}, else the legacy `zion_return_to`, removes both, and
 * passes the value through {@link resolvePostLoginPath}: sessionStorage is same-origin-writable,
 * so the stored value is validated again. A storage that throws reads as no value: `/home`.
 */
export function consumeReturnTo(): string {
  try {
    const value =
      sessionStorage.getItem(RETURN_TO_STORAGE_KEY) ?? sessionStorage.getItem(LEGACY_RETURN_TO_STORAGE_KEY);
    sessionStorage.removeItem(RETURN_TO_STORAGE_KEY);
    sessionStorage.removeItem(LEGACY_RETURN_TO_STORAGE_KEY);
    return resolvePostLoginPath(value);
  } catch {
    return DEFAULT_LOGIN_LANDING;
  }
}

/**
 * The sessionStorage key that holds the per-flow marker: a nonce this tab minted with
 * {@link mintFlowNonce} before it asked core-api for the IdP authorize URL (R-108). The
 * marker derives from nothing the OAuth call returns. core-api binds the nonce into the signed
 * `state` and sends it back as the callback's `nonce` query parameter. {@link storeOAuthFlow}
 * writes it on the login page, and {@link consumeOAuthFlow} reads and removes it on the callback
 * page. A callback whose `nonce` differs from the marker, or a tab with no marker, did not start
 * its flow here, so the callback page refuses the exchange (login CSRF).
 */
export const OAUTH_FLOW_MARKER_KEY = 'zion.oauth.flow';

/** The number of random bytes in one OAuth flow nonce: 16, so 32 hex characters. */
const OAUTH_FLOW_NONCE_BYTES = 16;

/**
 * Mint a fresh OAuth flow nonce: 16 bytes from `crypto.getRandomValues` as 32 lower-case hex
 * characters, the grammar core-api accepts (R-108). Every call answers a new value. Throws only
 * when the platform has no `crypto.getRandomValues`; the login page then shows its OAuth failure
 * and leaves the document nowhere. The name carries no `auth` substring because CodeQL's
 * sensitive-name heuristic reads one as a credential source (alert 453 on PR #3152).
 */
export function mintFlowNonce(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(OAUTH_FLOW_NONCE_BYTES));
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Record that this tab starts an OAuth flow, before the login page asks for the IdP URL. A
 * non-empty `nonce` is stored under {@link OAUTH_FLOW_MARKER_KEY}; `null` or an empty value
 * removes the key, so a marker from an earlier attempt cannot vouch for a later one. A storage
 * that throws (private mode, disabled storage) stores nothing, and the callback then refuses the
 * exchange: the failure closes the door, and the user starts again. Never throws.
 */
export function storeOAuthFlow(nonce: string | null): void {
  try {
    if (nonce !== null && nonce !== '') {
      sessionStorage.setItem(OAUTH_FLOW_MARKER_KEY, nonce);
      return;
    }
    sessionStorage.removeItem(OAUTH_FLOW_MARKER_KEY);
  } catch {
    // Storage unavailable: no marker, which the callback page reads as "not started here".
  }
}

/**
 * Read and remove the per-flow marker. Answers the stored nonce, or `null` when the key is
 * absent or empty. The key is gone after the call on every path, so one marker vouches for one
 * callback at most. A storage that throws answers `null`. Synchronous, and never throws, so the
 * callback page removes the marker before its first await.
 */
export function consumeOAuthFlow(): string | null {
  try {
    const marker = sessionStorage.getItem(OAUTH_FLOW_MARKER_KEY);
    sessionStorage.removeItem(OAUTH_FLOW_MARKER_KEY);
    return marker === null || marker === '' ? null : marker;
  } catch {
    return null;
  }
}

/**
 * The first value of the query parameter `name` in `search` (with or without its leading `?`),
 * percent-decoded once, or `null` when it is absent. The one place a page reads the query.
 */
export function readQueryParam(search: string, name: string): string | null {
  return new URLSearchParams(search).get(name);
}

/** The one member of `Location` {@link leaveDocument} uses. */
export interface DocumentExit {
  assign(url: string): void;
}

/**
 * Leave the document for an off-origin URL: the IdP's authorize URL, or a consent `redirectTo`.
 *
 * `url` must parse as an absolute URL with `new URL(url)`. A value that does not parse, or whose
 * scheme is `javascript:`, `data:`, `vbscript:`, `blob:` or `file:`, answers false and navigates
 * nowhere. Any other scheme passes, a private-use scheme included, because native OAuth clients
 * register one. On a pass the parsed `href` is assigned to `exit` (the window's location unless a
 * caller passes another) and the answer is true. Never throws.
 */
export function leaveDocument(url: string, exit: DocumentExit = window.location): boolean {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  if (REFUSED_SCHEMES.includes(parsed.protocol)) return false;
  exit.assign(parsed.href);
  return true;
}
